/**
 * KycPendingBanner — P0-1 Fix
 *
 * Shows when user is redirected from onboarding with pending KYC status.
 * Provides clear context about what's happening and what to expect.
 *
 * Design Canon: tokens-only, WCAG 2.2 AA, 240ms motion
 */

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { sfIcons } from "@/lib/sf-icons";
import { SFIcon } from "@/components/ui/sf-icon";
import { semanticTokens } from '@packages/config/theme';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { UX_LEXICON } from '@/lib/ux-lexicon';

interface KycPendingBannerProps {
  onRefresh?: () => void;
  onDismiss?: () => void;
  isRefreshing?: boolean;
}

export function KycPendingBanner({
  onRefresh,
  onDismiss,
  isRefreshing = false,
}: KycPendingBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <Alert
      className={cn(
        'relative mb-4',
        semanticTokens.warning.bg,
        semanticTokens.warning.border,
        semanticTokens.motion.standard
      )}
      role="status"
      aria-live="polite"
    >
      <SFIcon
        icon={sfIcons.clock}
        className={cn('h-5 w-5', semanticTokens.warning.accent)}
        aria-hidden="true"
      />
      <AlertTitle className={cn(semanticTokens.typography.h4, semanticTokens.warning.fg)}>
        {UX_LEXICON.kyc.processing.title}
      </AlertTitle>
      <AlertDescription className={cn(semanticTokens.typography.body, semanticTokens.warning.fg, 'mt-1')}>
        {UX_LEXICON.kyc.processing.description}
      </AlertDescription>

      <div className={cn('flex items-center gap-2 mt-3', semanticTokens.spacing.sm.replace('p-', 'gap-'))}>
        {onRefresh && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isRefreshing}
            className={cn('min-h-[36px]', semanticTokens.motion.standard, semanticTokens.focus.ring)}
            aria-label={UX_LEXICON.kyc.processing.action.refresh}
          >
            <SFIcon
              icon={sfIcons.refresh}
              className={cn('h-4 w-4 mr-2', isRefreshing && 'animate-spin')}
              aria-hidden="true"
            />
            {isRefreshing ? 'Checking...' : UX_LEXICON.kyc.processing.action.refresh}
          </Button>
        )}
      </div>

      {/* Dismiss button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleDismiss}
        className={cn(
          'absolute top-2 right-2 h-8 w-8',
          semanticTokens.motion.standard,
          semanticTokens.focus.ring
        )}
        aria-label="Dismiss notification"
      >
        <SFIcon icon={sfIcons.close} className="h-4 w-4" aria-hidden="true" />
      </Button>
    </Alert>
  );
}
